import * as E from 'fp-ts/lib/Either';
import * as T from 'fp-ts/lib/Task';
import * as O from 'fp-ts/lib/Option';

const isEither = <L, R>(value: unknown): value is E.Either<L, R> =>
  E.isLeft(value as E.Either<L, R>) || E.isRight(value as E.Either<L, R>);

const isTask = <R>(value: unknown): value is T.Task<R> =>
  typeof (value as T.Task<R>) === 'function' &&
  (value as T.Task<R>).length === 0;

interface OptionRes<T> {
  _tag?: 'None' | 'Some';
  value?: T;
}

const isOption = <O>(value: unknown): value is O.Option<O> & OptionRes<O> => {
  return typeof (value as OptionRes<O>)._tag === 'string';
};

export const unwrap = async <T = unknown>(response: unknown): Promise<T> => {
  if (isEither<Error, T>(response)) {
    if (E.isLeft(response)) {
      throw response.left;
    }
    if (E.isRight(response)) {
      return response.right;
    }
  }

  if (isTask(response)) {
    const result = await response();
    if (isEither<Error, T>(result)) {
      if (E.isLeft(result)) {
        throw result.left;
      }
      if (E.isRight(result)) {
        return result.right;
      }
    }
    return result as T;
  }

  return response as T;
};

export const unwrapOption = async <T = unknown, E = unknown>(
  response: unknown,
  errorResponse?: E
): Promise<T | E> => {
  if (isOption<T>(response)) {
    if (response._tag === 'None') {
      return errorResponse !== undefined ? errorResponse : (response._tag as E);
    }
    if (response._tag === 'Some') {
      return response.value as T;
    }
  }

  throw new Error('Invalid option');
};
